
/*
 * HOUSE OF FURBABY
 * Complete Their Look - Product Pages
 *
 * Priority:
 * 1. Search & Discovery complementary products
 * 2. Curated seasonal favorites collection
 */

(() => {
  const COLLECTION = 'cart-recommendation-favorites';

  const escapeHTML = value =>
    String(value ?? '').replace(/[&<>"']/g, char => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[char]);

  function isAvailable(product) {
    if (product.available === false) return false;
    if (product.available === true) return true;

    return Array.isArray(product.variants) &&
      product.variants.some(variant => variant.available);
  }

  function getPriceInCents(product, fromCollection) {
    const raw = product.price ??
      product.variants?.[0]?.price;

    if (raw == null) return null;

    const amount = Number(raw);
    if (!Number.isFinite(amount)) return null;

    // Shopify's collection JSON endpoint can return
    // dollar strings; recommendations JSON uses cents.
    return fromCollection
      ? Math.round(amount * 100)
      : amount;
  }

  async function loadCompleteLook(section) {
    const container = section.querySelector(
      '.hofb-complete-look__products'
    );
    const productId = section.dataset.productId;
    const limit = Number(section.dataset.limit) || 4;

    if (!container || !productId) return;

    const root = window.Shopify?.routes?.root || '/';
    const selected = [];
    const seen = new Set([String(productId)]);

    function addProducts(products, fromCollection) {
      for (const product of products) {
        if (selected.length >= limit) break;

        const id = String(product.id);
        if (seen.has(id) || !isAvailable(product)) continue;

        const price = getPriceInCents(
          product,
          fromCollection
        );

        if (price === null || !product.handle) continue;

        seen.add(id);
        selected.push({ product, price });
      }
    }

    // First: handpicked complementary products
    try {
      const url = new URL(
        root + 'recommendations/products.json',
        window.location.origin
      );

      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', limit);
      url.searchParams.set('intent', 'complementary');

      const response = await fetch(url);
      if (!response.ok) throw new Error('Recommendation request failed');

      const data = await response.json();
      addProducts(data.products || [], false);
    } catch (error) {
      console.warn('HOFB complementary products:', error);
    }

    // Second: fill empty spots with seasonal favorites
    if (selected.length < limit) {
      try {
        const url = new URL(
          root + 'collections/' + COLLECTION + '/products.json',
          window.location.origin
        );
        url.searchParams.set('limit', '100');

        const response = await fetch(url);
        if (!response.ok) throw new Error('Favorites request failed');

        const data = await response.json();
        addProducts(data.products || [], true);
      } catch (error) {
        console.warn('HOFB seasonal favorites:', error);
      }
    }

    if (!selected.length) {
      section.hidden = true;
      return;
    }

    const currency = window.Shopify?.currency?.active || 'USD';

    const formatMoney = cents =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency
      }).format(cents / 100);

    container.innerHTML = selected.map(({ product, price }) => {
      const image = product.featured_image ||
        product.images?.[0]?.src ||
        product.images?.[0] ||
        '';

      const imageURL = typeof image === 'string'
        ? image
        : image?.src || '';

      const title = escapeHTML(product.title);
      const handle = encodeURIComponent(product.handle);

      return `
        <a
          class="hofb-look-card"
          href="${escapeHTML(root)}products/${handle}"
        >
          <div class="hofb-look-card__image">
            ${imageURL ? `
              <img
                src="${escapeHTML(imageURL)}"
                alt="${title}"
                loading="lazy"
              >
            ` : ''}
          </div>

          <div class="hofb-look-card__info">
            <h3>${title}</h3>
            <p>${formatMoney(price)}</p>
            <span>Shop This Item →</span>
          </div>
        </a>
      `;
    }).join('');

    section.hidden = false;
  }

  function initialize(scope = document) {
    if (scope.matches?.('.hofb-complete-look')) {
      loadCompleteLook(scope);
    }

    scope.querySelectorAll?.('.hofb-complete-look')
      .forEach(loadCompleteLook);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', event => {
    initialize(event.target);
  });
})();
